import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { formatIDR, parseIDR } from "@/lib/format";
import { Button, Field, Input, Select, Modal } from "@/components/ui-kit";
import BulkImport from "@/components/BulkImport";
import MasterCombo from "@/components/MasterCombo";
import { PriceAdjustmentField } from "@/components/pricing/PriceAdjustmentField";
import { BulkPriceEditModal } from "@/components/pricing/BulkPriceEditModal";
import { GroupedMaterialEditor } from "@/components/pricing/GroupedMaterialEditor";
import { useTableFilter } from "@/hooks/useTableFilter";
import { Plus, Pencil, Trash2, Layers, Download, Image as ImageIcon, Sparkles, Layers2,
  ChevronDown, ChevronRight, MapPin, Package, Truck, Wrench } from "lucide-react";

/**
 * Iter v34aw · Batch 2 — Workspace Material Catalog: Grouped view.
 * Each material_name+unit is 1 group. Multiple locations become multi-entry
 * Ongkos Kirim inline. Preserves per-row CRUD (edit/delete). All backend
 * unchanged — grouping happens at frontend from existing /prices data.
 */

const blank = {
  delivery_location: "", material_name: "", unit: "",
  price_non_vat: 0, price_ex_vat: 0, price_with_vat: 0,
  delivery_cost: 0, unloading_fee: 0, category: "delivery"
};

export default function PriceList() {
  const { t } = useLang();
  const { hasPerm, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(blank);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [markedIds, setMarkedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [catalogEdit, setCatalogEdit] = useState(null);
  const [groupedEdit, setGroupedEdit] = useState(null);
  const [expanded, setExpanded] = useState({});  // groupKey -> bool
  const canEditCatalog = user?.role === "super_admin" || user?.is_engineer;
  const { filtered: textFiltered, FilterBar } = useTableFilter(rows, ["material_name", "delivery_location", "unit", "category"], "Cari material, lokasi, kategori…");
  const filtered = textFiltered.filter((r) =>
    (!categoryFilter || (r.category || "") === categoryFilter) &&
    (!locationFilter || (r.delivery_location || "") === locationFilter)
  );
  const uniqueLocations = Array.from(new Set(rows.map((r) => r.delivery_location).filter(Boolean))).sort();

  // Iter v34aw — Group by material_name+unit for the new grouped view.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const key = `${(r.material_name || "").toLowerCase().trim()}||${(r.unit || "").toLowerCase().trim()}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          material_name: r.material_name,
          unit: r.unit,
          category: r.category,
          variants: [],
        });
      }
      map.get(key).variants.push(r);
    }
    return Array.from(map.values()).map((g) => {
      // Primary variant = first (usually the "default" or oldest row).
      const prices = g.variants.map((v) => Number(v.price_non_vat) || 0);
      const exVats = g.variants.map((v) => Number(v.price_ex_vat) || 0);
      const withVats = g.variants.map((v) => Number(v.price_with_vat) || 0);
      const unloadings = g.variants.map((v) => Number(v.unloading_fee) || 0);
      const uniformNon = prices.every((p) => p === prices[0]);
      const uniformEx = exVats.every((p) => p === exVats[0]);
      const uniformWith = withVats.every((p) => p === withVats[0]);
      const uniformUnload = unloadings.every((p) => p === unloadings[0]);
      const anyHidden = g.variants.some((v) => !!v.catalog_hidden);
      return {
        ...g,
        price_non_vat: prices[0] || 0,
        price_ex_vat: exVats[0] || 0,
        price_with_vat: withVats[0] || 0,
        unloading_fee: unloadings[0] || 0,
        uniform_prices: uniformNon && uniformEx && uniformWith,
        uniform_unload: uniformUnload,
        deliveries: g.variants.map((v) => ({
          id: v.id,
          location: v.delivery_location || "Tanpa Lokasi",
          cost: Number(v.delivery_cost) || 0,
        })),
        any_hidden: anyHidden,
      };
    });
  }, [filtered]);

  const selectedRows = filtered.filter((r) => markedIds.includes(r.id));
  const allGroupIds = groups.flatMap((g) => g.variants.map((v) => v.id));
  const allSelected = allGroupIds.length > 0 && allGroupIds.every((id) => markedIds.includes(id));

  // Pagination on groups now (20/page)
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [textFiltered.length, categoryFilter, locationFilter]);
  const pageGroups = groups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const load = () => Promise.all([
    api.get("/prices"),
    api.get("/masters/categories"),
  ]).then(([p, c]) => { setRows(p.data); setCategories(c.data); });
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      let priceWithVat = parseIDR(form.price_with_vat);
      let priceExVat = parseIDR(form.price_ex_vat);
      if (priceWithVat > 0 && priceExVat === 0) {
        priceExVat = Math.round((priceWithVat / 1.11) * 100) / 100;
      } else if (priceExVat > 0 && priceWithVat === 0) {
        priceWithVat = Math.round(priceExVat * 1.11 * 100) / 100;
      }
      const payload = {
        ...form,
        client_name: "",
        price_non_vat: Math.round(parseIDR(form.price_non_vat)),
        price_ex_vat: priceExVat,
        price_with_vat: priceWithVat,
        delivery_cost: Math.round(parseIDR(form.delivery_cost)),
        unloading_fee: Math.round(parseIDR(form.unloading_fee)),
        catalog_hidden: !!form.catalog_hidden,
      };
      if (edit) await api.patch(`/prices/${edit.id}`, payload);
      else await api.post("/prices", payload);
      toast.success(t("save") + " ✓");
      setOpen(false); setEdit(null); setForm(blank); load();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus baris ini?")) return;
    await api.delete(`/prices/${id}`); toast.success("Dihapus"); load();
  };

  const delGroup = async (group) => {
    if (!window.confirm(`Hapus SEMUA baris material "${group.material_name}" (${group.variants.length} lokasi)?`)) return;
    try {
      for (const v of group.variants) {
        await api.delete(`/prices/${v.id}`);
      }
      toast.success(`Material "${group.material_name}" dihapus`);
      load();
    } catch { toast.error("Sebagian gagal dihapus"); }
  };

  const openEdit = (row) => { setEdit(row); setForm({ ...blank, ...row }); setOpen(true); };
  const openNew = () => { setGroupedEdit({ material_name: "", unit: "" }); };
  const toggleExpand = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const exportXlsx = async () => {
    try {
      const r = await api.get("/prices/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 12);
      a.download = `daftar-harga_${ts}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengekspor");
    }
  };

  return (
    <div className="min-h-full bg-[#f6f5f0]">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        {/* Bento header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 font-semibold">Master · Pricing</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-stone-900">{t("price_list")}</h1>
            <p className="text-stone-500 mt-1 text-sm">Master harga per material. Ongkos kirim bisa diisi &gt;1 (per lokasi). Perubahan harga otomatis tercatat.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {hasPerm("price.edit") && markedIds.length > 0 && (
              <Button onClick={() => setBulkOpen(true)} variant="secondary" data-testid="bulk-edit-btn">
                <Layers className="h-4 w-4" /> Edit Massal ({markedIds.length})
              </Button>
            )}
            {hasPerm("price.view") && (
              <Button variant="secondary" onClick={exportXlsx} data-testid="export-prices-btn"><Download className="h-4 w-4" /> Eksport Excel</Button>
            )}
            {hasPerm("price.create") && <BulkImport kind="prices" onDone={load} />}
            {hasPerm("price.create") && <Button onClick={openNew} data-testid="add-price-btn"><Plus className="h-4 w-4" />{t("add")}</Button>}
          </div>
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Total Material" value={groups.length} icon={Package} color="blue"/>
          <StatBox label="Total Baris Harga" value={rows.length} icon={Layers2} color="emerald"/>
          <StatBox label="Kategori" value={categories.length} icon={Sparkles} color="amber"/>
          <StatBox label="Lokasi Pengiriman" value={uniqueLocations.length} icon={MapPin} color="stone"/>
        </div>

        {/* Filter bar */}
        <div className="rounded-2xl bg-white border border-stone-200/70 p-4 flex items-center justify-between gap-3 flex-wrap sticky top-16 z-10 shadow-sm">
          <div className="flex-1 min-w-[240px]">{FilterBar}</div>
          <div className="flex items-center gap-2">
            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="!w-auto" data-testid="price-category-filter">
              <option value="">Semua Kategori</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
            <Select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} className="!w-auto" data-testid="price-location-filter">
              <option value="">Semua Lokasi</option>
              {uniqueLocations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
            </Select>
          </div>
        </div>

        {/* Grouped table */}
        <div className="rounded-2xl bg-white border border-stone-200/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
                  <th className="py-3 px-3 w-10">
                    {hasPerm("price.edit") && (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => setMarkedIds(e.target.checked ? allGroupIds : [])}
                        data-testid="price-select-all"
                        title="Pilih semua"
                      />
                    )}
                  </th>
                  <th className="py-3 px-3 w-6"></th>
                  <th className="py-3 px-3">{t("material_name")}</th>
                  <th className="py-3 px-3">{t("unit")}</th>
                  <th className="py-3 px-3 text-right">{t("price_non_vat")}</th>
                  <th className="py-3 px-3 text-right">{t("price_ex_vat")}</th>
                  <th className="py-3 px-3 text-right">{t("price_with_vat")}</th>
                  <th className="py-3 px-3">Ongkos Kirim (per Lokasi)</th>
                  <th className="py-3 px-3 text-right">{t("unloading_fee")}</th>
                  <th className="py-3 px-3">{t("category")}</th>
                  <th className="py-3 px-3 text-right">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 && (
                  <tr><td colSpan={11} className="py-16 text-center text-stone-400">Belum ada data material.</td></tr>
                )}
                {pageGroups.map((g, gidx) => {
                  const idx = (page - 1) * PAGE_SIZE + gidx;
                  const isExpanded = !!expanded[g.key];
                  const groupSelected = g.variants.every((v) => markedIds.includes(v.id));
                  return (
                    <React.Fragment key={g.key}>
                      {/* Main grouped row */}
                      <tr className={`border-b border-stone-100 ${groupSelected ? "bg-blue-50/40" : "hover:bg-stone-50/50"}`} data-testid={`price-group-${idx}`}>
                        <td className="py-3 px-3">
                          {hasPerm("price.edit") && (
                            <input
                              type="checkbox"
                              checked={groupSelected}
                              onChange={(e) => {
                                const ids = g.variants.map((v) => v.id);
                                setMarkedIds(e.target.checked
                                  ? Array.from(new Set([...markedIds, ...ids]))
                                  : markedIds.filter((x) => !ids.includes(x)));
                              }}
                              data-testid={`price-group-select-${idx}`}
                            />
                          )}
                        </td>
                        <td className="py-3 px-1">
                          <button onClick={() => toggleExpand(g.key)} className="text-stone-400 hover:text-stone-900" data-testid={`price-group-expand-${idx}`}>
                            {isExpanded ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                          </button>
                        </td>
                        <td className="py-3 px-3">
                          <div className="font-medium text-stone-900">{g.material_name}</div>
                          <div className="text-[10px] text-stone-500 mt-0.5">{g.variants.length} lokasi{g.any_hidden ? " · sebagian disembunyikan dari katalog" : ""}</div>
                        </td>
                        <td className="py-3 px-3 text-stone-600">{g.unit}</td>
                        <td className="py-3 px-3 text-right font-mono-num">
                          {formatIDR(g.price_non_vat)}
                          {!g.uniform_prices && <div className="text-[9px] text-amber-600">bervariasi</div>}
                        </td>
                        <td className="py-3 px-3 text-right font-mono-num">
                          {formatIDR(g.price_ex_vat)}
                          {!g.uniform_prices && <div className="text-[9px] text-amber-600">bervariasi</div>}
                        </td>
                        <td className="py-3 px-3 text-right font-mono-num font-medium">
                          {formatIDR(g.price_with_vat)}
                          {!g.uniform_prices && <div className="text-[9px] text-amber-600">bervariasi</div>}
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex flex-wrap gap-1 max-w-md" data-testid={`price-ongkos-${idx}`}>
                            {g.deliveries.length === 0 || g.deliveries.every((d) => d.cost === 0) ? (
                              <span className="text-[11px] text-stone-400 italic">Tidak ada</span>
                            ) : (
                              g.deliveries.slice(0, 3).map((d, i) => (
                                <span key={i} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-100" title={d.location}>
                                  <MapPin className="w-2.5 h-2.5"/>
                                  <span className="font-medium">{d.location}</span>
                                  <span className="font-mono-num">{formatIDR(d.cost)}</span>
                                </span>
                              ))
                            )}
                            {g.deliveries.length > 3 && (
                              <span className="text-[10px] text-stone-500 px-2 py-0.5">+{g.deliveries.length - 3} lagi</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right font-mono-num">
                          {formatIDR(g.unloading_fee)}
                          {!g.uniform_unload && <div className="text-[9px] text-amber-600">bervariasi</div>}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full ${g.category === "service" ? "bg-blue-50 text-blue-700 border border-blue-100" : "bg-emerald-50 text-emerald-700 border border-emerald-100"}`}>
                            {g.category}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          {hasPerm("price.edit") && (
                            <button onClick={() => setGroupedEdit({ material_name: g.material_name, unit: g.unit })}
                              className="text-stone-500 hover:text-blue-700 inline-flex items-center gap-1 px-2"
                              title="Edit Grup Material — semua lokasi sekaligus"
                              data-testid={`edit-grouped-${idx}`}>
                              <Layers2 className="h-3.5 w-3.5"/>
                            </button>
                          )}
                          {canEditCatalog && g.variants[0] && (
                            <button onClick={() => setCatalogEdit({ id: g.variants[0].id, material_name: g.material_name, image_b64: g.variants[0].image_b64 || "", description: g.variants[0].description || "" })}
                              className="text-stone-500 hover:text-emerald-700 inline-flex items-center gap-1 px-2"
                              title="Edit Katalog (gambar & deskripsi)"
                              data-testid={`edit-catalog-${idx}`}>
                              <ImageIcon className="h-3.5 w-3.5"/>
                            </button>
                          )}
                          {hasPerm("price.delete") && (
                            <button onClick={() => delGroup(g)}
                              className="text-stone-500 hover:text-red-700 inline-flex items-center gap-1 px-2"
                              title="Hapus SEMUA baris material ini"
                              data-testid={`delete-group-${idx}`}>
                              <Trash2 className="h-3.5 w-3.5"/>
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* Expanded — per-location variants */}
                      {isExpanded && g.variants.map((v, vi) => (
                        <tr key={v.id} className="border-b border-stone-100 bg-stone-50/60" data-testid={`price-variant-${idx}-${vi}`}>
                          <td className="py-2 px-3">
                            {hasPerm("price.edit") && (
                              <input
                                type="checkbox"
                                checked={markedIds.includes(v.id)}
                                onChange={(e) => setMarkedIds(e.target.checked ? [...markedIds, v.id] : markedIds.filter((x) => x !== v.id))}
                                data-testid={`price-select-${idx}-${vi}`}
                              />
                            )}
                          </td>
                          <td colSpan={2} className="py-2 px-3 pl-10">
                            <span className="text-[11px] text-stone-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-blue-700"/>
                              <span className="font-medium text-stone-800">{v.delivery_location || "Tanpa Lokasi"}</span>
                              {v.catalog_hidden && <span className="text-[9px] px-1.5 py-0 rounded bg-stone-200 text-stone-600 ml-1">Hidden</span>}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-stone-500 text-xs">{v.unit}</td>
                          <td className="py-2 px-3 text-right font-mono-num text-xs">{formatIDR(v.price_non_vat)}</td>
                          <td className="py-2 px-3 text-right font-mono-num text-xs">{formatIDR(v.price_ex_vat)}</td>
                          <td className="py-2 px-3 text-right font-mono-num text-xs">{formatIDR(v.price_with_vat)}</td>
                          <td className="py-2 px-3 font-mono-num text-xs">
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-100">
                              <Truck className="w-2.5 h-2.5"/> {formatIDR(v.delivery_cost)}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right font-mono-num text-xs">
                            <span className="inline-flex items-center gap-1 text-[10px] text-stone-700">
                              <Wrench className="w-2.5 h-2.5"/> {formatIDR(v.unloading_fee)}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-xs text-stone-500">{v.category}</td>
                          <td className="py-2 px-3 text-right">
                            {hasPerm("price.edit") && <button onClick={() => openEdit(v)} className="text-stone-500 hover:text-blue-700 inline-flex items-center gap-1 px-1.5" data-testid={`edit-price-${idx}-${vi}`}><Pencil className="h-3 w-3"/></button>}
                            {hasPerm("price.delete") && <button onClick={() => del(v.id)} className="text-stone-500 hover:text-red-700 inline-flex items-center gap-1 px-1.5" data-testid={`delete-price-${idx}-${vi}`}><Trash2 className="h-3 w-3"/></button>}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-stone-200 bg-stone-50/30 text-xs" data-testid="pricelist-pagination">
              <span className="text-stone-600">Halaman <b>{page}</b> dari <b>{totalPages}</b> · {groups.length} material</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" className="!px-2 !py-1" disabled={page <= 1} onClick={() => setPage(1)} data-testid="page-first">«</Button>
                <Button variant="ghost" className="!px-2 !py-1" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev">‹</Button>
                <input type="number" min={1} max={totalPages} value={page} onChange={(e) => {
                  const n = Math.max(1, Math.min(totalPages, Number(e.target.value) || 1));
                  setPage(n);
                }} className="w-14 text-center border border-stone-300 px-1 py-1 rounded" data-testid="page-input" />
                <Button variant="ghost" className="!px-2 !py-1" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next">›</Button>
                <Button variant="ghost" className="!px-2 !py-1" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="page-last">»</Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-[11px] text-stone-500 flex items-center gap-2 pl-1">
          <ChevronRight className="w-3 h-3"/> Klik ikon panah di kiri baris untuk expand ke detail per-lokasi. Klik ikon Layers untuk edit semua lokasi sekaligus.
        </p>

        <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("edit") + " " + t("price_list") : t("input_edit_price")} size="lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("delivery_location")} required>
              <Input value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} data-testid="form-delivery-location" />
            </Field>
            <Field label={t("material_name")} required hint="Sinkron dengan Stok Barang">
              <MasterCombo source="stocks" field="material_name" value={form.material_name} onChange={(v) => setForm({ ...form, material_name: v })} placeholder="Pilih dari stok…" data-testid="form-material-name" />
            </Field>
            <Field label={t("unit")} required>
              <MasterCombo source="units" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} placeholder="m3, kg, ton" data-testid="form-unit" />
            </Field>
            <Field label={t("category")}>
              <MasterCombo source="categories" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="delivery, service…" data-testid="form-category" />
            </Field>
            <div className="md:col-span-2 pt-2 border-t border-stone-200">
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">Harga & Biaya</p>
              <p className="text-[11px] text-stone-500 mb-3">Edit langsung kolom nilai, atau gunakan kontrol &quot;Arah Perubahan / Mode / Nilai&quot; lalu klik Terapkan untuk auto-hitung.</p>
            </div>
            <PriceAdjustmentField label={t("price_non_vat")} value={form.price_non_vat} onChange={(v) => setForm({ ...form, price_non_vat: Math.round(parseIDR(v)) })} testId="adj-price-non-vat" />
            <PriceAdjustmentField label={t("price_ex_vat")} value={form.price_ex_vat} onChange={(v) => {
              const num = parseIDR(v);
              const derived = num > 0 ? Math.round(num * 1.11 * 100) / 100 : 0;
              setForm({ ...form, price_ex_vat: v, price_with_vat: derived });
            }} testId="adj-price-ex-vat" />
            <PriceAdjustmentField label={t("price_with_vat")} value={form.price_with_vat} onChange={(v) => {
              const num = parseIDR(v);
              const derived = num > 0 ? Math.round((num / 1.11) * 100) / 100 : 0;
              setForm({ ...form, price_with_vat: v, price_ex_vat: derived });
            }} testId="adj-price-with-vat" />
            <div className="md:col-span-2 text-[11px] text-stone-500 bg-stone-50/40 border border-stone-200 px-3 py-2 rounded">
              <strong>Auto-sync:</strong> Edit &quot;Harga Exc PPN&quot; → &quot;Harga + PPN&quot; otomatis = harga × 1,11 · Edit &quot;Harga + PPN&quot; → &quot;Harga Exc PPN&quot; otomatis = harga ÷ 1,11.
            </div>
            <PriceAdjustmentField label={t("delivery_cost")} value={form.delivery_cost} onChange={(v) => setForm({ ...form, delivery_cost: Math.round(parseIDR(v)) })} testId="adj-delivery-cost" disabled={!hasPerm("price.edit_delivery_cost")} />
            {!hasPerm("price.edit_delivery_cost") && (
              <div className="md:col-span-2 text-[11px] text-amber-700 bg-amber-50/60 border border-amber-200 px-3 py-1.5 rounded">
                🔒 Edit Ongkos Kirim membutuhkan hak akses <code className="font-mono text-[10px]">price.edit_delivery_cost</code>.
              </div>
            )}
            <PriceAdjustmentField label={t("unloading_fee")} value={form.unloading_fee} onChange={(v) => setForm({ ...form, unloading_fee: Math.round(parseIDR(v)) })} testId="adj-unloading-fee" />
          </div>
          <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer" data-testid="price-catalog-toggle">
            <input
              type="checkbox"
              checked={!form.catalog_hidden}
              onChange={(e) => setForm({ ...form, catalog_hidden: !e.target.checked })}
              data-testid="price-catalog-visible-checkbox"
            />
            <span>Tampilkan di <b>Katalog Klien</b> <span className="text-stone-400 text-xs">(uncheck untuk menyembunyikan hanya baris ini)</span></span>
          </label>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-price-btn">{t("save")}</Button>
          </div>
        </Modal>

        <BulkPriceEditModal
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          selectedRows={selectedRows}
          onDone={() => { setMarkedIds([]); load(); }}
        />

        <CatalogEditorModal
          target={catalogEdit}
          onClose={() => setCatalogEdit(null)}
          onSaved={() => { setCatalogEdit(null); load(); }}
        />

        {groupedEdit && (
          <GroupedMaterialEditor
            materialName={groupedEdit.material_name}
            unit={groupedEdit.unit}
            categories={categories}
            onClose={() => setGroupedEdit(null)}
            onSaved={() => { setGroupedEdit(null); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    stone: "bg-stone-50 text-stone-700 border-stone-200",
  };
  return (
    <div className="rounded-2xl bg-white border border-stone-200/70 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${colorMap[color]}`}>
        <Icon className="w-4 h-4"/>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
        <div className="font-display text-2xl text-stone-900 leading-none mt-1">{value}</div>
      </div>
    </div>
  );
}

function CatalogEditorModal({ target, onClose, onSaved }) {
  const [tab, setTab] = useState("image");
  const [imgB64, setImgB64] = useState("");
  const [desc, setDesc] = useState("");
  const [aiBusy, setAiBusy] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) { setImgB64(target.image_b64 || ""); setDesc(target.description || ""); setTab("image"); }
  }, [target]);

  if (!target) return null;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Maks 3 MB"); return; }
    const r = new FileReader();
    r.onload = () => setImgB64(String(r.result || "").split(",")[1] || "");
    r.readAsDataURL(f);
  };

  const suggestImage = async () => {
    setAiBusy("image");
    try {
      const r = await api.post(`/prices/${target.id}/suggest-image`, null, { timeout: 60000 });
      setImgB64(r.data.image_b64 || "");
      toast.success("Gambar dihasilkan oleh AI (Nano Banana)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal generate gambar"); }
    finally { setAiBusy(""); }
  };
  const suggestDetail = async () => {
    setAiBusy("detail");
    try {
      const r = await api.post(`/prices/${target.id}/suggest-detail`, null, { timeout: 60000 });
      setDesc(r.data.description || "");
      toast.success("Deskripsi dihasilkan oleh AI (Claude Sonnet 4.5)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal generate deskripsi"); }
    finally { setAiBusy(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (imgB64 !== (target.image_b64 || "")) {
        if (imgB64) await api.patch(`/prices/${target.id}/image`, { image_b64: imgB64 });
      }
      if (desc !== (target.description || "")) {
        await api.patch(`/prices/${target.id}/detail`, { description: desc });
      }
      toast.success("Katalog tersimpan");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={!!target} onClose={onClose} title={`Edit Katalog — ${target.material_name}`} size="lg">
      <div className="space-y-4" data-testid="catalog-editor">
        <div className="flex gap-1 border-b border-stone-200">
          <button
            onClick={() => setTab("image")}
            className={`px-3 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 -mb-px ${tab === "image" ? "border-blue-500 text-blue-700" : "border-transparent text-stone-500 hover:text-stone-900"}`}
            data-testid="catalog-tab-image"
          >
            <ImageIcon className="h-3 w-3 inline mr-1" /> Gambar
          </button>
          <button
            onClick={() => setTab("detail")}
            className={`px-3 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 -mb-px ${tab === "detail" ? "border-blue-500 text-blue-700" : "border-transparent text-stone-500 hover:text-stone-900"}`}
            data-testid="catalog-tab-detail"
          >
            Deskripsi
          </button>
        </div>

        {tab === "image" && (
          <div className="space-y-3">
            <div className="aspect-video w-full bg-stone-50 border border-stone-200 rounded-xl flex items-center justify-center overflow-hidden">
              {imgB64 ? (
                <img src={`data:image/png;base64,${imgB64}`} alt="preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <p className="text-stone-400 text-sm">Belum ada gambar — upload atau pakai saran AI.</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="cursor-pointer text-xs uppercase tracking-wider border border-stone-300 hover:border-stone-500 px-3 py-1.5 rounded-full" data-testid="catalog-image-upload">
                <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                Upload Manual
              </label>
              <button
                onClick={suggestImage}
                disabled={aiBusy === "image"}
                className="text-xs uppercase tracking-wider bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white px-3 py-1.5 rounded-full disabled:opacity-50 flex items-center gap-1"
                data-testid="catalog-image-ai"
              >
                <Sparkles className="h-3 w-3" />
                {aiBusy === "image" ? "Generating…" : "Saran AI (Nano Banana)"}
              </button>
              {imgB64 && (
                <button onClick={() => setImgB64("")} className="text-xs uppercase tracking-wider text-red-700 hover:bg-red-50 border border-red-200 px-3 py-1.5 rounded-full" data-testid="catalog-image-clear">
                  Hapus
                </button>
              )}
            </div>
            <p className="text-[10px] text-stone-400">AI akan generate foto profesional bertema studio berdasarkan nama material & kategori. Hasil bisa di-regenerate berkali-kali.</p>
          </div>
        )}

        {tab === "detail" && (
          <div className="space-y-3">
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={8}
              className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-1 focus:outline-blue-500"
              placeholder="Deskripsi material yang akan ditampilkan di halaman detail katalog…"
              data-testid="catalog-detail-text"
            />
            <button
              onClick={suggestDetail}
              disabled={aiBusy === "detail"}
              className="text-xs uppercase tracking-wider bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white px-3 py-1.5 rounded-full disabled:opacity-50 flex items-center gap-1"
              data-testid="catalog-detail-ai"
            >
              <Sparkles className="h-3 w-3" />
              {aiBusy === "detail" ? "Generating…" : "Saran AI (Claude 4.5)"}
            </button>
            <p className="text-[10px] text-stone-400">{desc.length} / 4000 karakter</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-stone-100 pt-3">
          <Button variant="secondary" onClick={onClose} data-testid="catalog-editor-cancel">Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="catalog-editor-save">{saving ? "Menyimpan…" : "Simpan"}</Button>
        </div>
      </div>
    </Modal>
  );
}
